/**
 * Gemini Adapter
 * Implements ILLMAdapter for Google Gemini models
 */

import { GoogleGenAI, type Part } from '@google/genai';
import type { AdapterCapabilities, GenerateOptions, TokenUsage, ProviderConfig } from '@/types/llm';
import { BaseAdapter } from './BaseAdapter';
import {
  generateContentWithLongOutput,
  getActionableErrorInfo,
} from '@/services/llm/providers/gemini';
import { buildStepConfig, type StepName as ConfigStepName } from '@/config/models';
import { safeParseJsonObject } from '@/services/utils/jsonParser';
import { UserActionableError } from '@/services/utils/errors';
import { logger } from '@/services/utils/logger';
import i18n from '@/i18n';
import { findModel, parseCapabilities, type ModelCapabilities } from '../ModelCapabilities';

/**
 * Normalize a user-supplied Gemini base URL.
 *
 * The @google/genai SDK appends the API version segment (`/v1beta/models/...`)
 * to `httpOptions.baseUrl` itself. Users routinely paste a relay endpoint that
 * already ends in `/v1` or `/v1beta`, producing a doubled path like
 * `POST /v1/v1beta/models/...` that relays reject as an invalid URL (the single
 * largest configuration-related refinement failure in the 2026-06 report).
 * Strip any trailing slash and a trailing version segment so the SDK can build
 * the path correctly.
 */
export function normalizeGeminiBaseUrl(baseUrl: string): string {
  return baseUrl.replace(/\/+$/, '').replace(/\/v1(beta)?$/i, '');
}

/**
 * Get max output tokens - uses modelCaps if available, otherwise fallback
 */
function getMaxOutputTokens(modelCaps: ModelCapabilities | null): number {
  if (modelCaps?.maxOutputTokens) {
    return modelCaps.maxOutputTokens;
  }
  // Fallback: 65536 (Gemini default)
  return 65536;
}

/**
 * Gemini Adapter
 * Wraps existing Gemini client code with ILLMAdapter interface
 */
export class GeminiAdapter extends BaseAdapter {
  readonly type = 'gemini' as const;
  readonly model: string;

  private ai: GoogleGenAI;
  private modelCaps: ModelCapabilities | null = null;

  /**
   * Get capabilities - determined from modelCaps
   */
  get capabilities(): AdapterCapabilities {
    return {
      jsonMode: 'full_schema',
      audio: this.modelCaps?.audioInput ?? true,
      search: true, // Gemini supports SearchGrounding
    };
  }

  /**
   * Check if model supports thinking - uses metadata
   * Fallback: lite models don't support thinking
   */
  supportsThinking(): boolean {
    // Use metadata if available
    if (this.modelCaps) {
      return this.modelCaps.reasoning;
    }
    // Fallback: exclude lite models
    return !this.model.includes('lite');
  }

  /**
   * Get thinking parameter style for Gemini models
   * - Gemini 2.5 Flash: uses thinkingBudget (budget style)
   * - Gemini 3.0+: uses thinkingLevel (level style: 'none', 'low', 'medium', 'high')
   */
  getThinkingStyle(): 'budget' | 'level' {
    return this.model.includes('gemini-3') || this.model.includes('gemini-4') ? 'level' : 'budget';
  }

  constructor(config: ProviderConfig) {
    super(config);
    this.model = config.model;

    // Lookup model capabilities from models.json
    const matchResult = findModel(config.model);
    if (matchResult.model) {
      this.modelCaps = parseCapabilities(matchResult.model);
    }

    // Initialize Gemini client
    const clientOptions: { apiKey: string; baseUrl?: string } = {
      apiKey: config.apiKey,
    };

    if (config.baseUrl) {
      clientOptions.baseUrl = normalizeGeminiBaseUrl(config.baseUrl);
    }

    this.ai = new GoogleGenAI(clientOptions);
  }

  /**
   * Generate structured object response
   */
  async generateObject<T>(options: GenerateOptions): Promise<T> {
    if (!options.schema) {
      throw new Error(i18n.t('services:api.errors.schemaRequired'));
    }

    // Check audio capability if audio input is provided
    if (options.audio && !this.capabilities.audio) {
      throw new Error(i18n.t('services:api.errors.audioNotSupported', { model: this.model }));
    }

    const parts = this.buildParts(options);

    // Build config from stepName if provided, otherwise use defaults
    const stepConfig: ReturnType<typeof buildStepConfig> | Record<string, never> = options.stepName
      ? this.getStepConfig(options.stepName)
      : {};

    // Transform thinkingConfig based on model version
    const thinkingConfig = this.getThinkingConfig(stepConfig.thinkingConfig);

    try {
      // Use executeWithRetry for consistent retry/timeout handling
      const text = await this.executeWithRetry(
        () =>
          generateContentWithLongOutput(
            this.ai,
            this.model,
            options.systemInstruction || '',
            parts,
            options.schema,
            {
              maxOutputTokens: getMaxOutputTokens(this.modelCaps),
              // Pass through Gemini-specific settings from step config
              ...(stepConfig.safetySettings && { safetySettings: stepConfig.safetySettings }),
              ...(stepConfig.tools && { tools: stepConfig.tools }),
              ...(thinkingConfig && { thinkingConfig }),
            },
            options.signal,
            options.onUsage ? (usage: any) => this.mapUsage(usage, options.onUsage!) : undefined
          ),
        {
          signal: options.signal,
          timeoutMs: options.timeoutMs,
          retries: 3,
        }
      );

      return safeParseJsonObject<T>(text);
    } catch (error: any) {
      // Extract actionable error info if available
      const actionableInfo = getActionableErrorInfo(error);
      if (actionableInfo) {
        logger.error('Gemini generateObject failed with actionable error', {
          actionableMessage: actionableInfo.message,
          actionableCode: actionableInfo.code,
          originalError: error.message,
        });
        throw new UserActionableError(actionableInfo.message, actionableInfo.code);
      }
      throw error;
    }
  }

  /**
   * Build parts array for Gemini API
   */
  private buildParts(options: GenerateOptions): Part[] {
    const parts: Part[] = [{ text: options.prompt }];

    // Add audio if provided
    if (options.audio) {
      parts.push({
        inlineData: {
          mimeType: options.audio.mimeType,
          data: options.audio.data,
        },
      });
    }

    return parts;
  }

  /**
   * Map Gemini usage to standard TokenUsage
   */
  private mapUsage(geminiUsage: any, callback: (usage: TokenUsage) => void): void {
    const candidatesTokens = geminiUsage.candidatesTokens || geminiUsage.candidatesTokenCount || 0;
    callback({
      promptTokens: geminiUsage.promptTokens || 0,
      candidatesTokens,
      completionTokens: candidatesTokens,
      totalTokens: geminiUsage.totalTokens || 0,
      modelName: this.model,
    });
  }

  /**
   * Get step config from buildStepConfig helper
   * Maps LLM StepName to Config StepName
   */
  private getStepConfig(stepName: string): ReturnType<typeof buildStepConfig> {
    // Map LLM step names to config step names
    const stepMapping: Record<string, ConfigStepName> = {
      refinement: 'refinement',
      translation: 'translation',
      proofread: 'batchProofread',
      speakerExtraction: 'speakerProfile',
      glossaryExtraction: 'glossaryExtraction',
    };

    const configStepName = stepMapping[stepName] || 'refinement';
    return buildStepConfig(configStepName);
  }

  /**
   * Transform thinkingConfig based on model version
   * Gemini 2.5: use thinkingBudget (token count)
   * Gemini 3: use thinkingLevel (low/medium/high)
   */
  private getThinkingConfig(originalConfig?: {
    thinkingLevel?: string;
  }): { thinkingLevel?: string; thinkingBudget?: number } | undefined {
    if (!originalConfig?.thinkingLevel) return undefined;
    if (originalConfig.thinkingLevel === 'none') return undefined;

    // Check model capability using metadata
    if (!this.supportsThinking()) return undefined;

    const level = originalConfig.thinkingLevel as 'low' | 'medium' | 'high';

    if (this.getThinkingStyle() === 'budget') {
      // Gemini 2.5: convert to thinkingBudget
      const budgetMap = { low: 4096, medium: 8192, high: 16384 };
      return { thinkingBudget: budgetMap[level] || 8192 };
    } else {
      // Gemini 3+: use thinkingLevel directly
      return { thinkingLevel: level };
    }
  }
}
